import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';

import ReactDOM from 'react-dom';

import { Toast } from './Toast';
import type { ToastAction, ToastVariant } from './Toast';
import { ToastOverlay } from './ToastOverlay';

const DEFAULT_TOAST_DURATION = 5000;

export type ToastConfig = Readonly<{
	variant: ToastVariant;
	message: string | React.ReactNode;
	action?: ToastAction;
	bottomPosition?: string;
	duration?: number;
}>;

export type ToastContextValue = {
	showToast: (config: ToastConfig) => void;
	hideToast: () => void;
};

export const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Provider component that manages toast notifications.
 *
 * Toasts are rendered in a custom overlay that integrates with React Aria's
 * overlay system, ensuring they remain interactive even when modals are open.
 * Each toast auto-dismisses after a configurable duration (default 5 seconds).
 */
export const ToastProvider: React.FC<Readonly<{ children: React.ReactNode }>> = ({ children }) => {
	const [activeToast, setActiveToast] = useState<ToastConfig | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const hideToast = useCallback(() => {
		setActiveToast(null);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const showToast = useCallback((config: ToastConfig) => {
		// Clear any existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		setActiveToast(config);

		// Auto-hide after duration (default 5 seconds)
		const duration = config.duration ?? DEFAULT_TOAST_DURATION;
		timeoutRef.current = setTimeout(() => {
			setActiveToast(null);
			timeoutRef.current = null;
		}, duration);
	}, []);

	const handleActionClick = useCallback(() => {
		if (activeToast?.action?.onClick) {
			activeToast.action.onClick();
		}
		hideToast();
	}, [activeToast, hideToast]);

	const contextValue = useMemo(() => ({ showToast, hideToast }), [showToast, hideToast]);

	return (
		<ToastContext.Provider value={contextValue}>
			{children}
			{activeToast &&
				ReactDOM.createPortal(
					<ToastOverlay>
						<Toast
							variant={activeToast.variant}
							message={activeToast.message}
							action={activeToast.action}
							bottomPosition={activeToast.bottomPosition}
							onActionClick={handleActionClick}
							onClose={hideToast}
						/>
					</ToastOverlay>,
					document.body,
				)}
		</ToastContext.Provider>
	);
};
